package top.wcpe.allincore.ui.runtime.animation

import top.wcpe.allincore.dsl.ir.codec.ByteArrayIrReader
import top.wcpe.allincore.dsl.ir.codec.ByteArrayIrWriter
import top.wcpe.allincore.dsl.ir.codec.IrByteReader
import top.wcpe.allincore.dsl.ir.codec.IrByteWriter
import kotlin.jvm.JvmStatic

/**
 * `UiAction.Kind.STATE_TRANSITION` 的 bodyBytes 自定义编码（M10b）。
 *
 * 格式：`u16 fsmIdLen | UTF8 fsmId | u16 targetLen | UTF8 target`。
 * 由 runtime 编/解码，IR codec 仅原样透传字节流。
 *
 * FR-UI-021：java.io.Data(In/Out)putStream → dsl-ir 既有 commonMain 的 IrByteWriter/Reader
 * （writeShort/writeBytes/readUnsignedShort/readBytes 大端语义逐一对齐），wire 字节零变更。
 */
data class StateTransitionPayload(val fsmId: String, val target: String) {

    /** 编码为字节流。 */
    fun encode(): ByteArray {
        val out = ByteArrayIrWriter()
        writeLp(out, fsmId)
        writeLp(out, target)
        return out.toByteArray()
    }

    companion object {
        /** LP 字段字节长度上限（u16）。 */
        private const val MAX_FIELD_LEN = 0xFFFF

        /** 从字节流解码；格式错误时抛 [IllegalStateException]。 */
        @JvmStatic
        fun decode(data: ByteArray): StateTransitionPayload = try {
            val input = ByteArrayIrReader(data)
            val fsm = readLp(input)
            val target = readLp(input)
            StateTransitionPayload(fsm, target)
        } catch (e: IndexOutOfBoundsException) {
            // ByteArrayIrReader 截断/越界时直接数组访问抛 IndexOutOfBoundsException（含 JVM 的
            // ArrayIndexOutOfBoundsException 子类），与原 DataInputStream 的 IOException 越界语义对齐
            throw IllegalStateException("STATE_TRANSITION payload 解码失败", e)
        }

        private fun writeLp(out: IrByteWriter, s: String) {
            val bytes = s.encodeToByteArray()
            require(bytes.size <= MAX_FIELD_LEN) { "STATE_TRANSITION 字段长度超限: ${bytes.size}" }
            out.writeShort(bytes.size)
            out.writeBytes(bytes)
        }

        private fun readLp(input: IrByteReader): String {
            val len = input.readUnsignedShort()
            val bytes = ByteArray(len)
            input.readBytes(bytes)
            return bytes.decodeToString()
        }
    }
}
